import { Component, OnInit } from '@angular/core';
import { Config } from "../../core/DefaultConfig";
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { TranslateModule } from "@ngx-translate/core";
import { ApplicationDataService } from '../../core/services/application-data.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { AppUrlConstants } from '../../core/constants/AppUrlConstants';
import { GenericCrudService } from '../../core/services/generic-crud.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AdminStructure } from '../../core/constants/adminStructure.const';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-group-rights',
  standalone: true,
  imports: [
    MatCard, MatCardContent, MatError, MatFormField,
    MatLabel, MatOption, MatSelect, ReactiveFormsModule,
    TranslateModule, FormsModule
  ],
  templateUrl: './group-rights.component.html',
  styleUrls: ['./group-rights.component.css']
})
export class GroupRightsComponent implements OnInit {
  config = new Config();
  form!: FormGroup;

  PROCESS: string = 'PROCESS';
  adminStructure: any = AdminStructure;

  // ── Groups ────────────────────────────────────────────────────────────────
  groupsList: any[] = [];
  filteredgroupsList: any[] = [];

  // ── Source lists (ALL data pre-loaded on init) ────────────────────────────
  // divisionList → ALL divisions
  // districtList → ALL districts (sab divisions ke)
  // tehsilList   → ALL tehsils   (sab districts ke)
  // mouzaList    → ALL mouzas    (sab tehsils ke)
  divisionList: any[] = [];
  districtList: any[] = [];
  tehsilList: any[] = [];
  mouzaList: any[] = [];
  workFlowStepsList: any[] = [];

  // ── Assigned (right panel) ────────────────────────────────────────────────
  selectedDivisionList: any[] = [];
  selectedDistrictList: any[] = [];
  selectedTehsilList: any[] = [];
  selectedMouzaList: any[] = [];
  selectedWorkFlowStepsList: any[] = [];

  // ── Available (left panel) ────────────────────────────────────────────────
  // display lists ab parent-filter se banti hain:
  //   displayDistrictList → sirf assigned divisions ke districts
  //   displayTehsilList   → sirf assigned districts ke tehsils
  //   displayMouzaList    → sirf assigned tehsils ke mouzas
  displayDivisionList: any[] = [];
  displayDistrictList: any[] = [];
  displayTehsilList: any[] = [];
  displayMouzaList: any[] = [];
  displayWorkFlowStepsList: any[] = [];

  hasUnsavedChanges = false;

  // ── Search terms ──────────────────────────────────────────────────────────
  availableSearchProcess = '';
  assignedSearchProcess = '';
  availableSearchDivision = '';
  assignedSearchDivision = '';
  availableSearchDistrict = '';
  assignedSearchDistrict = '';
  availableSearchTehsil = '';
  assignedSearchTehsil = '';
  availableSearchMouza = '';
  assignedSearchMouza = '';

  // ── Checkbox state ────────────────────────────────────────────────────────
  checkedAvailable: { [type: string]: any[] } = {};
  checkedAssigned: { [type: string]: any[] } = {};

  // ── Loading states ────────────────────────────────────────────────────────
  isInitialLoading = false;
  isLoadingGroup   = false;
  isSaving         = false;

  constructor(
    private formBuilder: FormBuilder,
    private appDataService: ApplicationDataService,
    private authService: AuthenticationService,
    private crudService: GenericCrudService,
    public dialog: MatDialog
  ) {}

  // ──────────────────────────────────────────────────────────────────────────
  ngOnInit() {
    this.form = this.formBuilder.group({
      groupId:          [null],
      workFlowSteps:    [null],
      divisions:        [null],
      districts:        [null],
      tehsil:           [null],
      mouza:            [null],
      divisionsIds:     [null],
      districtsIds:     [null],
      tehsilIds:        [null],
      mouzaIds:         [null],
      workFlowStepsIds: [null],
    });

    // Sab data pehle load karo (forkJoin + chain)
    this.loadAllData();

    // Group select hone par permissions load karo
    this.f['groupId'].valueChanges.subscribe((value: any) => {
      if (value) {
        this.loadGroupPermissions(value);
      }
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP 1: forkJoin se Groups + WorkflowSteps + Divisions ek saath load karo
  //         phir chain mein Districts → Tehsils → Mouzas load karo
  // ──────────────────────────────────────────────────────────────────────────
  private loadAllData(): void {
    this.isInitialLoading = true;

    forkJoin({
      groups:        this.crudService.getAll(AppUrlConstants.GROUP_URL + AppUrlConstants.LIST),
      workflowSteps: this.appDataService.getAllWorkFlowStepsBySegment(this.authService.getUserSegment()),
      divisions:     this.appDataService.getByType(this.adminStructure.DIVISION)
    }).subscribe({
      next: (result: any) => {
        // Groups — Public User remove karo
        let groups: any[] = result.groups?.data || [];
        const idx = groups.findIndex((g: any) => g.name === 'Public User');
        if (idx > -1) groups.splice(idx, 1);
        this.groupsList         = groups;
        this.filteredgroupsList = [...groups];

        // Workflow Steps
        this.workFlowStepsList = result.workflowSteps?.data || [];
        this.display(this.PROCESS);

        // Divisions
        this.divisionList = result.divisions?.data || [];
        this.display(this.adminStructure.DIVISION);

        // Chain: sab divisions ke districts load karo
        if (this.divisionList.length > 0) {
          const divisionIds = this.divisionList.map((d: any) => d.adminStrId);
          this.loadAllChildItems(divisionIds, 'districts');
        } else {
          this.isInitialLoading = false;
        }
      },
      error: (err) => {
        console.error('Initial load error', err);
        this.openAlert('Failed to load data');
        this.isInitialLoading = false;
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP 2: Chain loader
  //   parentIds deke ALL districts, tehsils, mouzas ek baar mein load karo
  //   ye data sirf source list mein store hota hai
  //   display() parent-filter se decide karta hai kya dikhana hai
  // ──────────────────────────────────────────────────────────────────────────
  private loadAllChildItems(parentIds: number[], level: 'districts' | 'tehsils' | 'mouzas'): void {
    this.appDataService.getAdminStructureByParentIds(parentIds).subscribe({
      next: (result: any) => {
        const items: any[] = result?.data || [];

        if (level === 'districts') {
          this.districtList = items;
          this.display(this.adminStructure.DISTRICT);

          if (items.length > 0) {
            this.loadAllChildItems(items.map((d: any) => d.adminStrId), 'tehsils');
          } else {
            this.isInitialLoading = false;
          }

        } else if (level === 'tehsils') {
          this.tehsilList = items;
          this.display(this.adminStructure.TEHSIL);

          if (items.length > 0) {
            this.loadAllChildItems(items.map((t: any) => t.adminStrId), 'mouzas');
          } else {
            this.isInitialLoading = false;
          }

        } else if (level === 'mouzas') {
          this.mouzaList = items;
          this.display(this.adminStructure.MOUZA);
          this.isInitialLoading = false;
        }
      },
      error: (err) => {
        console.error(`Error loading ${level}`, err);
        this.isInitialLoading = false;
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Group permissions load karo (valueChanges + discardChanges dono mein use)
  // ──────────────────────────────────────────────────────────────────────────
  private loadGroupPermissions(groupId: number): void {
    this.isLoadingGroup    = true;
    this.hasUnsavedChanges = false;

    this.appDataService.getPermissionByGroup(groupId).subscribe({
      next: (res: any) => {
        this.isLoadingGroup = false;

        if (res?.data) {
          this.selectedDivisionList      = res.data.divisions     || [];
          this.selectedDistrictList      = res.data.districts     || [];
          this.selectedTehsilList        = res.data.tehsil        || [];
          this.selectedMouzaList         = res.data.mouza         || [];
          this.selectedWorkFlowStepsList = res.data.workFlowSteps || [];

          this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, this.PROCESS));
          this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList,      this.adminStructure.DIVISION));
          this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList,      this.adminStructure.DISTRICT));
          this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList,           this.adminStructure.TEHSIL));
          this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList,             this.adminStructure.MOUZA));
        } else {
          // Koi permission nahi — sab clear karo
          this.selectedDivisionList      = [];
          this.selectedDistrictList      = [];
          this.selectedTehsilList        = [];
          this.selectedMouzaList         = [];
          this.selectedWorkFlowStepsList = [];
          this.f['divisionsIds'].setValue(null);
          this.f['districtsIds'].setValue(null);
          this.f['tehsilIds'].setValue(null);
          this.f['mouzaIds'].setValue(null);
          this.f['workFlowStepsIds'].setValue(null);
        }

        // Sab panels refresh karo
        this.display(this.PROCESS);
        this.display(this.adminStructure.DIVISION);
        this.display(this.adminStructure.DISTRICT);
        this.display(this.adminStructure.TEHSIL);
        this.display(this.adminStructure.MOUZA);
        this.clearAllChecked();
        this.hasUnsavedChanges = false;
      },
      error: (err) => {
        this.isLoadingGroup = false;
        console.error('Permission load error', err);
        this.openAlert('Failed to load group permissions');
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // display() — KEY METHOD
  //   DIVISION → all - assigned
  //   DISTRICT → parent division assigned ho + not already assigned
  //   TEHSIL   → parent district assigned ho + not already assigned
  //   MOUZA    → parent tehsil assigned ho + not already assigned
  // ──────────────────────────────────────────────────────────────────────────
  private display(type: string): void {
    let newElements: any[] = [];

    if (type === this.PROCESS) {
      const selectedIds = this.f['workFlowStepsIds']?.value || [];
      (this.workFlowStepsList || []).forEach((u: any) => {
        if (!selectedIds.includes(u.complaintProcessId)) newElements.push(u);
      });
      this.displayWorkFlowStepsList = newElements;

    } else if (type === this.adminStructure.DIVISION) {
      const selectedIds = this.f['divisionsIds']?.value || [];
      (this.divisionList || []).forEach((u: any) => {
        if (!selectedIds.includes(u.adminStrId)) newElements.push(u);
      });
      this.displayDivisionList = newElements;

    } else if (type === this.adminStructure.DISTRICT) {
      // CORE LOGIC: sirf wo districts dikhao
      // jinka parent division assigned panel mein ho
      const assignedDistrictIds = this.f['districtsIds']?.value || [];
      const assignedDivisionIds = this.f['divisionsIds']?.value || [];
      (this.districtList || []).forEach((u: any) => {
        const parentAssigned = assignedDivisionIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedDistrictIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayDistrictList = newElements;

    } else if (type === this.adminStructure.TEHSIL) {
      // Sirf wo tehsils dikhao jinka parent district assigned ho
      const assignedTehsilIds   = this.f['tehsilIds']?.value    || [];
      const assignedDistrictIds = this.f['districtsIds']?.value || [];
      (this.tehsilList || []).forEach((u: any) => {
        const parentAssigned = assignedDistrictIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedTehsilIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayTehsilList = newElements;

    } else if (type === this.adminStructure.MOUZA) {
      // Sirf wo mouzas dikhao jinka parent tehsil assigned ho
      const assignedMouzaIds  = this.f['mouzaIds']?.value  || [];
      const assignedTehsilIds = this.f['tehsilIds']?.value || [];
      (this.mouzaList || []).forEach((u: any) => {
        const parentAssigned = assignedTehsilIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedMouzaIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayMouzaList = newElements;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Search filter helper
  // ──────────────────────────────────────────────────────────────────────────
  filterBySearch(list: any[], search: string): any[] {
    if (!search || !list) return list || [];
    return list.filter(item => item.name?.toLowerCase().includes(search.toLowerCase()));
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Checkbox helpers
  // ──────────────────────────────────────────────────────────────────────────
  getItemId(item: any, type: string): any {
    return type === this.PROCESS ? item.complaintProcessId : item.adminStrId;
  }

  isCheckedAvailable(item: any, type: string): boolean {
    const id = this.getItemId(item, type);
    return (this.checkedAvailable[type] || []).includes(id);
  }

  isCheckedAssigned(item: any, type: string): boolean {
    const id = this.getItemId(item, type);
    return (this.checkedAssigned[type] || []).includes(id);
  }

  toggleAvailableCheck(item: any, type: string): void {
    const id = this.getItemId(item, type);
    if (!this.checkedAvailable[type]) this.checkedAvailable[type] = [];
    const idx = this.checkedAvailable[type].indexOf(id);
    if (idx >= 0) { this.checkedAvailable[type].splice(idx, 1); }
    else           { this.checkedAvailable[type].push(id); }
  }

  toggleAssignedCheck(item: any, type: string): void {
    const id = this.getItemId(item, type);
    if (!this.checkedAssigned[type]) this.checkedAssigned[type] = [];
    const idx = this.checkedAssigned[type].indexOf(id);
    if (idx >= 0) { this.checkedAssigned[type].splice(idx, 1); }
    else           { this.checkedAssigned[type].push(id); }
  }

  clearAllChecked(): void {
    this.checkedAvailable = {};
    this.checkedAssigned  = {};
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Arrow button: Available → Assigned
  // ──────────────────────────────────────────────────────────────────────────
  moveToAssigned(type: string): void {
    const checkedIds = this.checkedAvailable[type] || [];
    if (checkedIds.length === 0) return;

    if (type === this.PROCESS) {
      const items = this.displayWorkFlowStepsList.filter(i => checkedIds.includes(i.complaintProcessId));
      this.selectedWorkFlowStepsList.push(...items);
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));

    } else if (type === this.adminStructure.DIVISION) {
      const items = this.displayDivisionList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedDivisionList.push(...items);
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      this.display(this.adminStructure.DISTRICT);

    } else if (type === this.adminStructure.DISTRICT) {
      const items = this.displayDistrictList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedDistrictList.push(...items);
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      this.display(this.adminStructure.TEHSIL);

    } else if (type === this.adminStructure.TEHSIL) {
      const items = this.displayTehsilList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedTehsilList.push(...items);
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      this.display(this.adminStructure.MOUZA);

    } else if (type === this.adminStructure.MOUZA) {
      const items = this.displayMouzaList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedMouzaList.push(...items);
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }

    this.checkedAvailable[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Arrow button: Assigned → Available  (cascade remove bhi hoga)
  // ──────────────────────────────────────────────────────────────────────────
  moveToAvailable(type: string): void {
    const checkedIds = this.checkedAssigned[type] || [];
    if (checkedIds.length === 0) return;

    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = this.selectedWorkFlowStepsList
        .filter(i => !checkedIds.includes(i.complaintProcessId));
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));

    } else if (type === this.adminStructure.DIVISION) {
      // Division hata → uske districts/tehsils/mouzas bhi hato (cascade)
      this.selectedDivisionList = this.selectedDivisionList
        .filter(i => !checkedIds.includes(i.adminStrId));
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveDivision(id));

    } else if (type === this.adminStructure.DISTRICT) {
      // District hata → uske tehsils/mouzas bhi hato (cascade)
      this.selectedDistrictList = this.selectedDistrictList
        .filter(i => !checkedIds.includes(i.adminStrId));
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveDistrict(id));

    } else if (type === this.adminStructure.TEHSIL) {
      // Tehsil hata → uske mouzas bhi hato
      this.selectedTehsilList = this.selectedTehsilList
        .filter(i => !checkedIds.includes(i.adminStrId));
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveTehsil(id));

    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList = this.selectedMouzaList
        .filter(i => !checkedIds.includes(i.adminStrId));
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }

    this.checkedAssigned[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Cascade delete helpers
  // ──────────────────────────────────────────────────────────────────────────
  private cascadeRemoveDivision(divisionId: number): void {
    // Is division ke sab assigned districts nikalo phir unke children bhi
    const childDistrictIds = this.selectedDistrictList
      .filter(d => d.parentAdminStrId === divisionId)
      .map(d => d.adminStrId);

    this.selectedDistrictList = this.selectedDistrictList
      .filter(d => d.parentAdminStrId !== divisionId);
    this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, this.adminStructure.DISTRICT));

    childDistrictIds.forEach(id => this.cascadeRemoveDistrict(id));
    this.display(this.adminStructure.DISTRICT);
  }

  private cascadeRemoveDistrict(districtId: number): void {
    // Is district ke sab assigned tehsils nikalo phir unke children bhi
    const childTehsilIds = this.selectedTehsilList
      .filter(t => t.parentAdminStrId === districtId)
      .map(t => t.adminStrId);

    this.selectedTehsilList = this.selectedTehsilList
      .filter(t => t.parentAdminStrId !== districtId);
    this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, this.adminStructure.TEHSIL));

    childTehsilIds.forEach(id => this.cascadeRemoveTehsil(id));
    this.display(this.adminStructure.TEHSIL);
  }

  private cascadeRemoveTehsil(tehsilId: number): void {
    this.selectedMouzaList = this.selectedMouzaList
      .filter(m => m.parentAdminStrId !== tehsilId);
    this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, this.adminStructure.MOUZA));
    this.display(this.adminStructure.MOUZA);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Bulk: Select All (Available → Assigned)
  // ──────────────────────────────────────────────────────────────────────────
  selectAll(type: string): void {
    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = [...this.workFlowStepsList];
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));

    } else if (type === this.adminStructure.DIVISION) {
      this.selectedDivisionList = [...this.divisionList];
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      this.display(this.adminStructure.DISTRICT);

    } else if (type === this.adminStructure.DISTRICT) {
      // Sirf wo add karo jo display mein hain (parent-filtered)
      this.selectedDistrictList.push(...this.displayDistrictList);
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      this.display(this.adminStructure.TEHSIL);

    } else if (type === this.adminStructure.TEHSIL) {
      this.selectedTehsilList.push(...this.displayTehsilList);
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      this.display(this.adminStructure.MOUZA);

    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList.push(...this.displayMouzaList);
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }

    this.checkedAvailable[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Bulk: Remove All (Assigned panel clear + cascade)
  // ──────────────────────────────────────────────────────────────────────────
  removeAll(type: string): void {
    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = [];
      this.f['workFlowStepsIds'].setValue([]);

    } else if (type === this.adminStructure.DIVISION) {
      // Division sab clear → neche sab bhi clear
      this.selectedDivisionList = [];
      this.selectedDistrictList = [];
      this.selectedTehsilList   = [];
      this.selectedMouzaList    = [];
      this.f['divisionsIds'].setValue([]);
      this.f['districtsIds'].setValue([]);
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.DISTRICT);
      this.display(this.adminStructure.TEHSIL);
      this.display(this.adminStructure.MOUZA);

    } else if (type === this.adminStructure.DISTRICT) {
      // District sab clear → tehsil + mouza bhi clear
      this.selectedDistrictList = [];
      this.selectedTehsilList   = [];
      this.selectedMouzaList    = [];
      this.f['districtsIds'].setValue([]);
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.TEHSIL);
      this.display(this.adminStructure.MOUZA);

    } else if (type === this.adminStructure.TEHSIL) {
      // Tehsil sab clear → mouza bhi clear
      this.selectedTehsilList = [];
      this.selectedMouzaList  = [];
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.MOUZA);

    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList = [];
      this.f['mouzaIds'].setValue([]);
    }

    this.checkedAssigned[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Save — isSaving guard laga hua hai
  // ──────────────────────────────────────────────────────────────────────────
  updateRights(): void {
    if (this.isSaving) return;
    this.isSaving = true;

    this.crudService.edit(this.form.value, AppUrlConstants.PERMISSION_URL + AppUrlConstants.ADD).subscribe({
      next: (res: any) => {
        this.isSaving = false;

        // Server se updated lists wapas set karo
        this.selectedWorkFlowStepsList = res?.data?.workFlowSteps || [];
        this.selectedDivisionList      = res?.data?.divisions     || [];
        this.selectedDistrictList      = res?.data?.districts     || [];
        this.selectedTehsilList        = res?.data?.tehsil        || [];
        this.selectedMouzaList         = res?.data?.mouza         || [];

        this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, this.PROCESS));
        this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList,          this.adminStructure.DIVISION));
        this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList,          this.adminStructure.DISTRICT));
        this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList,               this.adminStructure.TEHSIL));
        this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList,                 this.adminStructure.MOUZA));

        this.display(this.PROCESS);
        this.display(this.adminStructure.DIVISION);
        this.display(this.adminStructure.DISTRICT);
        this.display(this.adminStructure.TEHSIL);
        this.display(this.adminStructure.MOUZA);

        this.hasUnsavedChanges = false;
        this.clearAllChecked();
        this.openAlert('Rights updated successfully');
      },
      error: (e) => {
        this.isSaving = false;
        if (e?.status === 409) {
          if (e?.error?.errors != null) {
            for (const property in e.error.errors) {
              this.openAlert(e.error.errors[property]);
            }
          } else {
            this.openAlert(e?.error?.message);
          }
        }
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Discard — loadGroupPermissions() se fresh data aata hai
  // ──────────────────────────────────────────────────────────────────────────
  discardChanges(): void {
    const groupId = this.f['groupId'].value;
    if (!groupId) return;
    this.loadGroupPermissions(groupId);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Alert dialog
  // ──────────────────────────────────────────────────────────────────────────
  openAlert(message: string): void {
    this.dialog.open(AlertDialogComponent, {
      data: { message: message, buttonText: { cancel: 'OK' } }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Group search
  // ──────────────────────────────────────────────────────────────────────────
  onFilteredListSearch(event: any, originalList: any[], property: string): any[] {
    if (event && event.target?.value) {
      return originalList.filter(item =>
        item[property].toLowerCase().includes(event.target.value.toLowerCase())
      );
    }
    return originalList;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // ID extractor
  // ──────────────────────────────────────────────────────────────────────────
  private getIds(list: any[], type: string): any[] {
    const ids: any[] = [];
    if (type === this.PROCESS) {
      list?.forEach((i: any) => ids.push(i?.complaintProcessId));
    } else {
      list?.forEach((i: any) => ids.push(i?.adminStrId));
    }
    return ids;
  }
}